import React, { useState } from "react";
import { X } from "lucide-react";
import axios from "axios";
import "../../css/tasks/taskform.css";

export default function TaskForm({ onClose, onTaskCreated }) {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    priority: "medium",
    dueDate: "",
    assignedTo: "",
    type: "lead",
    customType: "",
    itemId: "",
  });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userLoading, setUserLoading] = useState(true);
  const [errors, setErrors] = useState({});

  React.useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setUserLoading(true);
      const response = await axios.get("https://apicrm.zenrealestate.in/api/users", {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      });

      // Handle different response formats
      let allUsers = response.data.data || response.data;

      // Filter for agents
      const agents = allUsers.filter(user => {
        const userRole = user.role ? user.role.toLowerCase() : "";
        return userRole === "agent";
      });

      console.log("Fetched users:", allUsers);
      console.log("Filtered agents:", agents);

      setUsers(agents);
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    } finally {
      setUserLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = "Task title is required";
    if (!formData.assignedTo) newErrors.assignedTo = "Please assign to an agent";
    if (formData.type === "other" && !formData.customType.trim())
      newErrors.customType = "Please specify the type";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      await axios.post("https://apicrm.zenrealestate.in/api/tasks", formData, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      });
      onTaskCreated();
    } catch (error) {
      console.error("Error creating task:", error);
      setErrors({ submit: error.response?.data?.message || "Error creating task" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tf-modal-overlay" onClick={onClose}>
      <div className="tf-modal" onClick={(e) => e.stopPropagation()}>
        <div className="tf-modal-header">
          <h2 className="tf-modal-title">Create New Task</h2>
          <button className="tf-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="tf-form">
          <div className="tf-form-group">
            <label className="tf-label">Task Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className={"tf-input " + (errors.title ? "error" : "")}
              placeholder="Enter task title"
            />
            {errors.title && <span className="tf-error">{errors.title}</span>}
          </div>

          <div className="tf-form-group">
            <label className="tf-label">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="tf-textarea"
              placeholder="Enter task description"
              rows="4"
            />
          </div>

          <div className="tf-form-row">
            <div className="tf-form-group">
              <label className="tf-label">Type *</label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="tf-select"
              >
                <option value="lead">Lead</option>
                <option value="property">Property</option>
                <option value="siteVisit">Site Visit</option>
                <option value="call">Call</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="tf-form-group">
              <label className="tf-label">Priority *</label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                className="tf-select"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>

          </div>

          {formData.type === "other" && (
            <div className="tf-form-group">
              <label className="tf-label">Specify Type *</label>
              <input
                type="text"
                name="customType"
                value={formData.customType}
                onChange={handleChange}
                className={"tf-input " + (errors.customType ? "error" : "")}
                placeholder="Enter the task type"
              />
              {errors.customType && <span className="tf-error">{errors.customType}</span>}
            </div>
          )}

          <div className="tf-form-group">
            <label className="tf-label">Assign To *</label>
            {userLoading ? (
              <div className="tf-select" style={{ opacity: 0.6 }}>Loading agents...</div>
            ) : (
              <>
                <select
                  name="assignedTo"
                  value={formData.assignedTo}
                  onChange={handleChange}
                  className={"tf-select " + (errors.assignedTo ? "error" : "")}
                >
                  <option value="">{users.length === 0 ? "No agents available" : "Select an agent"}</option>
                  {users.map(user => (
                    <option key={user._id} value={user._id}>
                      {user.Name}
                    </option>
                  ))}
                </select>
                {users.length === 0 && (
                  <span className="tf-error" style={{ marginTop: "4px", display: "block" }}>
                    No agents found in the system
                  </span>
                )}
              </>
            )}
            {errors.assignedTo && <span className="tf-error">{errors.assignedTo}</span>}
          </div>

          <div className="tf-form-row">
            <div className="tf-form-group">
              <label className="tf-label">Assign Date</label>
              <input
                type="date"
                value={new Date().toISOString().slice(0, 10)}
                className="tf-input"
                readOnly
                disabled
              />
            </div>

            <div className="tf-form-group">
              <label className="tf-label">Target Date</label>
              <input
                type="date"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleChange}
                className="tf-input"
              />
            </div>
          </div>

          {errors.submit && <div className="tf-error-message">{errors.submit}</div>}

          <div className="tf-form-actions">
            <button
              type="button"
              className="tf-btn-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="tf-btn-submit"
              disabled={loading}
            >
              {loading ? "Creating..." : "Create Task"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
